import React, { useEffect, useState } from 'react';
import { OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { OpModal } from 'components/customAntd/DLS/OpModal/OpModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { VisitorWorkflowFields } from 'types/visitorWorkflowTypes';
import { searchVisitors, searchVisitorsToday } from 'store/slices/visitorSearchSlice';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { VisitorType } from 'types/VisitorTypeTypes';

interface ModalFormProps {
    open: boolean;
    isInviting: boolean;
    isCheckrInstalled: boolean;
    signInWorkflowFields?: VisitorWorkflowFields;
    visitor?: any | null;
    visitors: any[];
    visitorTypes: VisitorType[];
    locationVisitorTypes?: VisitorType[];
    selectedVisitorTypeId?: number;
    onVisitorTypeChange?: (visitorTypeId: any) => void;
    onOk: (data: any) => void;
    onClose: () => void;

}

const VisitorListModal: React.FC<ModalFormProps> = ({ open, isInviting, isCheckrInstalled, signInWorkflowFields, visitor, visitors, visitorTypes, selectedVisitorTypeId, locationVisitorTypes, onVisitorTypeChange, onOk, onClose }) => {
    const { t } = useTranslation();
    const dispatch: AppDispatch = useDispatch();
    const [form] = OpForm.useForm();

    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const lastSearchParams = useSelector((state: RootState) => state.visitorSearch.lastSearchParams);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const { invitationConfig } = useSelector((state: RootState) => state.visitorInvitation);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const hasVisitorTypeSignInWorkflow = (locationVisitorTypes ?? []).some(
        (type) => type.workflow?.SignInWorkflowId && type.workflow.SignInWorkflowId !== -1
    );

    useEffect(() => {
        if (selectedVisitorTypeId !== undefined) {
            form.setFieldsValue({ visitorTypeId: selectedVisitorTypeId });
        }
    }, [selectedVisitorTypeId, form]);

    useEffect(() => {
        if (!open) {
            setIsSubmitting(false);
        }
    }, [open]);

    const handleSubmit = async () => {
        if (isSubmitting) return;

        try {
            const values = await form.validateFields();
            setIsSubmitting(true);

            const trimmedValues = Object.fromEntries(
                Object.entries(values).map(([key, value]) => [
                    key,
                    typeof value === 'string' ? value.trim() : value,
                ])
            );

            await onOk(trimmedValues);
            await dispatch(searchVisitorsToday({ orgId }));

            if (Object.keys(lastSearchParams).length !== 0) {
                await dispatch(searchVisitors({
                    ...lastSearchParams,
                    orgId,
                    siteId: lastSearchParams.siteId || globalLocationId!
                }));
            }

            form.resetFields();
            setIsSubmitting(false);
            onClose();
        } catch (errorInfo) {
            // Validation failed - error messages will be shown under form fields
            // Do not close modal
            setIsSubmitting(false);
        }
    };

    return (
        <OpModal
            open={open}
            title={visitor ? t('visitors.visitorList.edit') : t('visitors.visitorList.add')}
            okText={t('visitors.visitorList.save')}
            cancelText={t('visitors.visitorList.cancel')}
            onCancel={() => {
                form.resetFields();
                onClose();
            }}
            onOk={handleSubmit}
            centered
            closable={!isSubmitting}
            maskClosable={!isSubmitting}
            okButtonProps={{ disabled: isSubmitting, loading: isSubmitting }}
            cancelButtonProps={{ disabled: isSubmitting }}
            data-testid="add-visitor-save-button"
        >
            <OpForm
                form={form}
                onSubmit={() => { console.log("") }}
                hasError={false}
                defaultButtons={false}
                initialValues={visitor ? visitor : undefined}
                disabled={isSubmitting}
            >
                    <OpForm.Input
                        label={t('visitors.visitorList.firstName')} name="firstName"
                        rules={[{ required: true, message: t('visitors.visitorList.firstNameRequired') }]}
                    />
                    <OpForm.Input
                        label={t('visitors.visitorList.lastName')} name="lastName"
                        rules={[{ required: true, message: t('visitors.visitorList.lastNameRequired') }]}
                    />

                    {visitors.length === 0 && hasVisitorTypeSignInWorkflow && (
                        <OpForm.Select
                            label={t('signInManuallyDrawer.visitorType.label')}
                            name="visitorTypeId"
                            rules={[{ required: true, message: t('signInManuallyDrawer.visitorType.required') }]}
                            placeholder={t('signInManuallyDrawer.visitorType.placeholder')}
                            options={visitorTypes.map(type => ({
                                label: t(type.languageCode ?? ''),
                                value: type.id
                            }))}
                            onChange={value => {
                                form.setFieldsValue({ visitorTypeId: value });
                                onVisitorTypeChange?.(value);
                            }}
                        />
                    )}

                    {((!isInviting && signInWorkflowFields?.middleName.included === 1)) && (
                        <OpForm.Input
                            label={t('visitors.visitorList.middleName')} name="middleName"
                            rules={[
                                {
                                    required: (!isInviting && signInWorkflowFields?.middleName.required === 1),
                                    message: t('visitors.visitorList.middleNameRequired')
                                }
                            ]}
                        />
                    )}
                    
                    {((isInviting && invitationConfig?.email.included === 1) || (!isInviting && signInWorkflowFields?.email.included === 1)) && (
                        <OpForm.Input
                            label={t('visitors.visitorList.email')}
                            name="email"
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[
                                {
                                    required: (isInviting && invitationConfig?.email.required === 1) ||
                                        (!isInviting && signInWorkflowFields?.email.required === 1),
                                    message: t('visitors.visitorList.emailRequired')
                                },
                                {
                                    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                    message: t('visitors.visitorList.emailInvalid')
                                }
                            ]}
                        />
                    )}

                    {(isCheckrInstalled) && (
                        <OpForm.DatePicker
                            label={t('visitors.visitorList.dob')}
                            name="dob"
                            format="YYYY-MM-DD"
                            disabledDate={(current) => current && current > dayjs().endOf('day')}
                        />
                    )}

                    {((isInviting && invitationConfig?.visitPhone.included === 1) || (!isInviting && signInWorkflowFields?.visitPhone.included === 1)) && (
                        <OpForm.Input
                            label={t('visitors.visitorList.mobilePhone')} name="mobilePhone"
                            rules={[
                                {
                                    required: (isInviting && invitationConfig?.visitPhone.required === 1) ||
                                        (!isInviting && signInWorkflowFields?.visitPhone.required === 1),
                                    message: t('visitors.visitorList.mobilePhoneRequired')
                                }
                            ]}
                        />
                    )}

            </OpForm>
        </OpModal>
    );
};

export default VisitorListModal;
